import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchBar from '../../component/Geospatial/Searchbar';
import FilterBar from '../../component/Geospatial/Filterbar';
import FacilityMap from '../../component/Geospatial/Facilitymap';
import FacilityDetailPanel from '../../component/Geospatial/Facilitydetailpanel';
import { facilitiesApi } from '../../api/facilitiesApi';
import { marketsApi } from '../../api/marketsApi';
import { normalizeGpsPoint } from '../../services/gpsCoordinates';
import { withDisplayPoints } from '../../component/Geospatial/displayPoints';
import { useCollectionSync } from '../../hooks/useCollectionSync';
import { labelForValue, MARKET_SCHEDULE_OPTIONS, MARKET_TYPE_OPTIONS } from '../../component/MarketTrad/marketTradOptions';

const MARKET_TYPE_TO_GEO_TYPE = {
  processing_market: 'Processing',
  abattoir_market: 'Abattoir',
};

const matchesQuery = (record, query) =>
  [
    record.name,
    record.state,
    record.lga,
    record.ward,
    record.type,
    record.market_type_label,
    record.market_schedule_label,
    record.description,
  ].some((value) => String(value ?? '').toLowerCase().includes(query));

// Human labels for the per-facility-type fields stored in capacity_metadata
// (same field set FacilityCoreRegistry's edit form collects).
const CAPACITY_FIELD_LABELS = {
  daily_slaughter_capacity:   { label: 'Daily Slaughter',   unit: '/day' },
  annual_production_capacity: { label: 'Annual Production', unit: '' },
  cold_storage_capacity:      { label: 'Cold Storage',      unit: ' Tonnes' },
  cold_storage_type:          { label: 'Cold Storage Type', unit: '' },
  daily_milk_capacity_litres: { label: 'Daily Milk',        unit: ' L/day' },
  water_source:                { label: 'Water Source',     unit: '' },
};

// capacity_metadata is a structured object, not a { label, name } pair — build a
// readable summary from whichever of its fields are actually present instead of
// falling back to the record's notes (which may just hold rejection/assessment text).
function summarizeCapacity(metadata) {
  if (!metadata || typeof metadata !== 'object') return '';
  return Object.entries(CAPACITY_FIELD_LABELS)
    .map(([key, { label, unit }]) => {
      const value = metadata[key];
      if (value === undefined || value === null || value === '') return null;
      return `${label}: ${value}${unit}`;
    })
    .filter(Boolean)
    .join(' · ');
}

function mapFacilityRow(row = {}) {
  const point = normalizeGpsPoint(
    row.gps_coordinates ??
    row.gpsCoordinates ??
    row.coordinates ??
    row.point_geojson ??
    null
  );

  return {
    kind: 'facility',
    id: row.id ?? row.uuid ?? null,
    uuid: row.uuid ?? row.id ?? null,
    name: row.name ?? '',
    type: row.facilityType ?? row.facility_type ?? '',
    state: row.state ?? '',
    lga: row.lga ?? '',
    ward: row.ward ?? row.metadata?.ward ?? '',
    capacity: summarizeCapacity(row.capacity_metadata),
    status: row.functionalStatus ?? row.functional_status ?? row.status ?? 'Unknown',
    lat: point?.lat ?? null,
    lng: point?.lng ?? null,
    description: row.assessment_notes ?? row.notes ?? '',
    established: row.created_at ?? row.updated_at ?? '',
    contact: row.licensedStatus ?? row.licensed_status ?? '',
    facilityType: row.facilityType ?? row.facility_type ?? '',
    functionalStatus: row.functionalStatus ?? row.functional_status ?? '',
    ownership: row.ownership ?? '',
    upgradePriority: row.upgradePriority ?? row.upgrade_priority ?? '',
    licensedStatus: row.licensedStatus ?? row.licensed_status ?? '',
    gps_coordinates: row.gps_coordinates ?? row.gpsCoordinates ?? '',
    raw: row,
  };
}

function mapMarketRow(row = {}) {
  const point = normalizeGpsPoint(
    row.gps_coordinates ??
    row.gpsCoordinates ??
    row.coordinates ??
    row.point_geojson ??
    null
  );

  const marketType = row.market_type ?? row.marketType ?? '';
  const marketSchedule = row.market_schedule ?? row.marketSchedule ?? '';
  const geoType =
    MARKET_TYPE_TO_GEO_TYPE[String(marketType).trim().toLowerCase()] ??
    'Market';

  return {
    kind: 'market',
    id: row.uuid ?? row.id ?? row.market_uuid ?? null,
    uuid: row.uuid ?? row.id ?? row.market_uuid ?? null,
    name: row.market_name ?? row.marketName ?? row.name ?? '',
    type: geoType,
    state: row.state ?? '',
    lga: row.lga ?? '',
    ward: row.ward ?? '',
    capacity: row.capacity ?? '',
    status: row.status ?? 'Unknown',
    lat: point?.lat ?? null,
    lng: point?.lng ?? null,
    description: row.description ?? '',
    established: row.established ?? '',
    contact: row.contact ?? '',
    location: row.location ?? '',
    market_type: marketType,
    market_type_label: labelForValue(MARKET_TYPE_OPTIONS, marketType),
    market_schedule: marketSchedule,
    market_schedule_label: labelForValue(MARKET_SCHEDULE_OPTIONS, marketSchedule),
    tradingDays: row.trading_days ?? row.tradingDays ?? '',
    gps_coordinates: row.gps_coordinates ?? row.gpsCoordinates ?? '',
    raw: row,
  };
}

export default function GeospatialOverview() {
  const [activeType, setActiveType] = useState(null);
  const [selectedFacility, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');

    const [facilityResult, marketResult] = await Promise.allSettled([
      facilitiesApi.listAll({ per_page: 500 }),
      marketsApi.listAll({ per_page: 500 }),
    ]);

    const nextRecords = [];
    const errors = [];

    if (facilityResult.status === 'fulfilled') {
      const items = Array.isArray(facilityResult.value?.items) ? facilityResult.value.items : [];
      nextRecords.push(...items.map(mapFacilityRow).filter((item) => item.name));
    } else {
      errors.push('facility records');
    }

    if (marketResult.status === 'fulfilled') {
      const items = Array.isArray(marketResult.value?.items) ? marketResult.value.items : [];
      nextRecords.push(...items.map(mapMarketRow).filter((item) => item.name));
    } else {
      errors.push('market records');
    }

    setRecords(withDisplayPoints(nextRecords));

    if (errors.length === 2) {
      setError('Failed to load geospatial facility and market data.');
    } else if (errors.length === 1) {
      setError(`Loaded with partial data. Could not load ${errors[0]}.`);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useCollectionSync([facilitiesApi.collectionKey, marketsApi.collectionKey], loadRecords);

  const filtered = useMemo(() => {
    const byType = activeType ? records.filter((record) => record.type === activeType) : records;
    const q = query.trim().toLowerCase();
    return q ? byType.filter((record) => matchesQuery(record, q)) : byType;
  }, [activeType, records, query]);

  const handleSearch = (raw) => {
    setQuery(raw);
    const q = raw.trim().toLowerCase();
    if (!q) return;

    const pool = activeType ? records.filter((record) => record.type === activeType) : records;
    const hit = pool.find((record) => matchesQuery(record, q));
    if (hit) setSelected(hit);
  };

  const handleTypeChange = (type) => {
    setActiveType(type);
    setSelected(null);
  };

  return (
    <div className="min-h-screen bg-white px-4 py-4 font-sans lg:px-6 lg:py-5">
      <div className="mb-4">
        <h1 className="text-xl font-black text-gray-900 leading-none">Geospatial Overview</h1>
        <p className="mt-1 text-xs text-gray-400">
          Facility and market distribution across Nigeria. Filter by type or search, then click a marker for details.
        </p>
      </div>

      <SearchBar onSearch={handleSearch} />
      <FilterBar activeType={activeType} onTypeChange={handleTypeChange} />

      {error && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 lg:h-[600px] lg:flex-row">
        <div className="flex h-[420px] min-w-0 flex-col sm:h-[480px] lg:h-full lg:flex-1">
          <FacilityMap
            activeType={activeType}
            facilities={filtered}
            selectedFacility={selectedFacility}
            onSelectFacility={setSelected}
            onTypeClick={(type) => setActiveType((prev) => (prev === type ? null : type))}
            loading={loading}
          />
        </div>

        <aside className="w-full shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white lg:h-full lg:w-[340px]">
          <FacilityDetailPanel facility={selectedFacility} onClose={() => setSelected(null)} />
        </aside>
      </div>
    </div>
  );
}
